import Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  CLASS_IDS,
  DATA,
  OBSTACLES,
  itemBonuses,
  mergeBonuses,
  resetActiveObstacles,
  setActiveObstacles,
  shopBonuses,
  type AbilitySlot,
  type ClassId,
  type CombatFx,
  type PlayerInput,
} from '@gladiators/shared';
import { Connection } from './net/connection.js';
import { LAG_MS } from './env.js';
import type { GameState, PlayerView, StateMap } from './net/state.js';
import { LocalController } from './game/localPlayer.js';
import { SnapshotBuffer, type EntityPose } from './game/snapshots.js';
import { ArenaScene } from './scenes/arena.js';
import type { RuntimeContext } from './game/context.js';
import { renderStart, renderLobby, renderSelect, renderHud, renderMessage, clearUi } from './ui.js';

const MATCH_PHASES = new Set(['match', 'wavebreak', 'gameover', 'victory']);

interface NetSnapshot {
  players: Map<string, EntityPose>;
  enemies: Map<number, EntityPose>;
  localPose: { x: number; y: number; lastSeq: number } | null;
}

class App {
  private conn = new Connection();
  private room: Room<GameState> | null = null;
  private ctx: RuntimeContext | null = null;
  private game: Phaser.Game | null = null;
  private leaving = false;
  private pingTimer: number | null = null;
  private authStamp = 0;
  /** feature 5.2 — the boss id whose pillar obstacle set is currently applied to
   * the shared pathfinding / collision state (`null` = the base arena). Only
   * re-applied when this changes, so `render()` (~20/s) does not thrash the
   * cached blocked grid. */
  private appliedBossObstacles: string | null = null;

  start(): void {
    this.showStart();
  }

  // ------------------------------------------------------------- start screen

  private showStart(error = ''): void {
    this.teardown();
    renderStart(
      {
        onCreate: (name) => this.connect(() => this.conn.createRoom(name)),
        onJoin: (name, code) => this.connect(() => this.conn.joinRoom(code, name)),
      },
      error,
    );
  }

  private async connect(factory: () => Promise<Room<GameState>>): Promise<void> {
    renderStart({ onCreate: () => undefined, onJoin: () => undefined }, '', true);
    try {
      const room = await factory();
      this.setupRoom(room);
    } catch (err) {
      this.showStart(err instanceof Error ? err.message : 'Could not connect to the server.');
    }
  }

  // ------------------------------------------------------------- room wiring

  private setupRoom(room: Room<GameState>): void {
    this.room = room;
    this.leaving = false;
    this.ctx = {
      room,
      sessionId: room.sessionId,
      local: new LocalController(),
      snaps: new SnapshotBuffer(),
      ping: 0,
      lagMs: LAG_MS,
      onFx: null,
      localAuth: null,
      sendInput: (input: PlayerInput) => this.deliver(() => room.send('input', input)),
      sendCast: (slot: AbilitySlot, x: number, y: number) =>
        this.deliver(() => room.send('cast', { slot, x, y })),
    };

    room.onStateChange((state) => {
      // Extract a synchronous plain snapshot, then (optionally) delay its use so
      // the dev lag switch delays *incoming* state as well as outgoing input.
      const snap = this.extract(state);
      this.render(state);
      this.deliver(() => this.ingest(snap));
    });

    room.onMessage('fx', (list: CombatFx[]) => this.deliver(() => this.ctx?.onFx?.(list)));
    room.onMessage('pong', (sent: number) => {
      this.ctx!.ping = this.ctx!.ping * 0.7 + (Date.now() - sent) * 0.3;
    });
    room.onMessage('roomClosed', (msg: { reason?: string }) => {
      this.leaving = true;
      renderMessage('Room closed', msg.reason ?? 'The host closed the room.', () =>
        this.showStart(),
      );
    });

    room.onLeave((code) => {
      if (this.leaving) return;
      const msg =
        code === 4001
          ? 'That match has already started.'
          : 'You were disconnected from the server.';
      this.showStart(msg);
    });
    room.onError((_c, message) => this.showStart(message ?? 'Connection error.'));

    this.pingTimer = window.setInterval(() => {
      try {
        room.send('ping', Date.now());
      } catch {
        /* ignore */
      }
    }, 2000);

    if (room.state) this.render(room.state);
  }

  /** Run `fn` now, or after `lagMs` when the dev latency switch is on. */
  private deliver(fn: () => void): void {
    if (this.ctx && this.ctx.lagMs > 0) window.setTimeout(fn, this.ctx.lagMs);
    else fn();
  }

  private extract(state: GameState): NetSnapshot {
    const players = new Map<string, EntityPose>();
    let localPose: { x: number; y: number; lastSeq: number } | null = null;
    state.players.forEach((p, id) => {
      players.set(id, { x: p.x, y: p.y, facing: p.facing });
      if (id === this.ctx?.sessionId) localPose = { x: p.x, y: p.y, lastSeq: p.lastSeq };
    });
    const enemies = new Map<number, EntityPose>();
    state.enemies.forEach((e) => {
      enemies.set(e.id, { x: e.x, y: e.y, facing: e.facing });
    });
    return { players, enemies, localPose };
  }

  private ingest(snap: NetSnapshot): void {
    if (!this.ctx) return;
    this.ctx.snaps.push(snap.players, snap.enemies);
    if (snap.localPose) {
      this.ctx.localAuth = { ...snap.localPose, stamp: ++this.authStamp };
    }
  }

  // ------------------------------------------------------------- rendering

  private render(state: GameState): void {
    if (this.leaving) return; // a later patch must not clobber the "room closed" screen
    const players = playerList(state);
    const me = players.find((p) => p.id === this.ctx?.sessionId);
    const isHost = state.hostId === this.ctx?.sessionId;

    const inMatch = MATCH_PHASES.has(state.phase);
    this.setCanvasVisible(inMatch);

    if (state.phase === 'lobby') {
      this.render3d(false);
      renderLobby(state.code, players, isHost, state.difficulty, {
        onStart: () => this.room?.send('start'),
        onLeave: () => this.leaveToStart(),
        onSetDifficulty: (tier) => this.room?.send('setDifficulty', { tier }),
      });
      return;
    }

    if (state.phase === 'select' || state.phase === 'countdown') {
      this.render3d(false);
      renderSelect(players, this.ctx!.sessionId, state.countdown, state.phase, {
        onSelect: (cls) => this.room?.send('select', { cls }),
        onLock: () => this.room?.send('lock'),
        onUnlock: () => this.room?.send('unlock'),
        onLeave: () => this.leaveToStart(),
      });
      return;
    }

    // feature 5.2 — keep the client's pathfinding / collision obstacle set in
    // sync with a boss fight's bespoke pillars so click-to-move prediction
    // matches the server. Derived from the synced state: a live boss with a
    // known pillar set → base + pillars, else the base arena. Applied only on a
    // change (boss appears / boss gone) so `render()` does not bump the obstacle
    // generation — and rebuild the cached blocked grid — every patch.
    let bossId: string | null = null;
    for (const [, e] of state.enemies) {
      if (e.boss && (DATA.bosses[e.bossId]?.pillars?.length ?? 0) > 0) {
        bossId = e.bossId;
        break;
      }
    }
    if (bossId !== this.appliedBossObstacles) {
      if (bossId) setActiveObstacles(OBSTACLES.concat(DATA.bosses[bossId]!.pillars ?? []));
      else resetActiveObstacles();
      this.appliedBossObstacles = bossId;
    }

    // match / wavebreak / gameover
    if (me?.cls && CLASS_IDS.includes(me.cls as ClassId)) {
      this.ctx!.local.configure(me.cls as ClassId);
      // feature 2.3 / 3.1 — fold the shop + held-item move-speed bonuses into the
      // local predictor so click-to-move prediction matches the server's
      // `baseMoveSpeed`.
      const speedBonus = mergeBonuses(
        shopBonuses(me.shopBuys),
        itemBonuses(me.inventory),
      ).bonusSpeed;
      this.ctx!.local.speed = DATA.players.moveSpeed + speedBonus;
    }
    this.render3d(true);
    renderHud(
      me,
      players,
      state.wave,
      state.phase,
      state.countdown,
      isHost,
      this.ctx!.ping,
      state.difficulty,
      state.revivalTokens,
      {
        onRestart: () => this.room?.send('restart'),
        onQuit: () => this.room?.send('quit'),
        onLeave: () => this.leaveToStart(),
        onReady: () => this.room?.send('ready'),
        onBuy: (itemId) => this.room?.send('buy', { itemId }),
        onBuyItem: (itemId) => this.room?.send('buyItem', { itemId }),
        onDropItem: (slot) => this.room?.send('dropItem', { slot }),
        onForge: (recipeId) => this.room?.send('forge', { recipeId }),
        onSpendAbilityPoint: (slot) => this.room?.send('spendAbilityPoint', { slot }),
        onSpendTalentPoint: (nodeId) => this.room?.send('spendTalentPoint', { nodeId }),
        onAllocateAttr: (attr) => this.room?.send('allocateAttr', { attr }),
        onRespec: () => this.room?.send('respec'),
      },
    );
  }

  private render3d(active: boolean): void {
    if (!active) {
      if (this.game) {
        window.removeEventListener('resize', this.onResize);
        this.game.destroy(true);
        this.game = null;
      }
      return;
    }
    if (active && !this.game && this.ctx) {
      this.game = new Phaser.Game({
        type: Phaser.AUTO,
        parent: 'game',
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundColor: '#14161c',
        pixelArt: false,
        scene: [],
        fps: { target: 60 },
      });
      this.game.registry.set('ctx', this.ctx);
      this.game.scene.add('arena', ArenaScene, true);
      window.addEventListener('resize', this.onResize);
    }
  }

  private onResize = (): void => {
    this.game?.scale.resize(window.innerWidth, window.innerHeight);
  };

  private setCanvasVisible(v: boolean): void {
    const g = document.getElementById('game');
    if (g) g.style.display = v ? 'block' : 'none';
  }

  // ------------------------------------------------------------- teardown

  private leaveToStart(): void {
    this.leaving = true;
    void this.conn.leave();
    this.showStart();
  }

  private teardown(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    window.removeEventListener('resize', this.onResize);
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
    }
    this.ctx = null;
    this.room = null;
    clearUi();
    this.setCanvasVisible(false);
  }
}

/** feature 2.3 — flatten a colyseus `MapSchema<number>` (item id -> count) to a
 * plain record for the HUD. The `PlayerView` field is typed as the flat record;
 * the live value here is still the raw schema map, hence the cast. */
function mapToRecord(m: PlayerView['shopBuys']): Record<string, number> {
  const out: Record<string, number> = {};
  (m as unknown as StateMap<number> | undefined)?.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

/** feature 4.2 — flatten the synced `abilityPoints` array ([q, w, e, r]) to the
 * slot-keyed record the HUD reads. The live schema value is an ArraySchema, so
 * `Array.from` normalises it (and an undefined during the first patch). */
function abilityPointsOf(raw: PlayerView['abilityPoints']): PlayerView['abilityPoints'] {
  const a = Array.from((raw ?? []) as unknown as ArrayLike<number>);
  return { q: a[0] ?? 0, w: a[1] ?? 0, e: a[2] ?? 0, r: a[3] ?? 0 };
}

/** feature 4.4 — flatten the synced `allocatedAttr` array ([str, agi, ene]) to
 * the attribute-keyed record the HUD reads. */
function allocatedAttrOf(raw: PlayerView['allocatedAttr']): PlayerView['allocatedAttr'] {
  const a = Array.from((raw ?? []) as unknown as ArrayLike<number>);
  return { strength: a[0] ?? 0, agility: a[1] ?? 0, energy: a[2] ?? 0 };
}

function playerList(state: GameState): PlayerView[] {
  const out: PlayerView[] = [];
  state.players.forEach((p) => {
    out.push({
      id: p.id,
      name: p.name,
      cls: p.cls,
      locked: p.locked,
      host: p.host,
      ready: p.ready,
      x: p.x,
      y: p.y,
      hp: p.hp,
      maxHp: p.maxHp,
      mana: p.mana,
      maxMana: p.maxMana,
      facing: p.facing,
      dead: p.dead,
      moving: p.moving,
      gold: p.gold,
      xp: p.xp,
      level: p.level,
      skillPoints: p.skillPoints,
      freeAttrPoints: p.freeAttrPoints,
      abilityPoints: abilityPointsOf(p.abilityPoints),
      talentNodes: Array.from(p.talentNodes ?? []),
      allocatedAttr: allocatedAttrOf(p.allocatedAttr),
      strength: p.strength,
      agility: p.agility,
      energy: p.energy,
      abilityCd: Array.from(p.abilityCd ?? []),
      shopBuys: mapToRecord(p.shopBuys),
      inventory: Array.from(p.inventory ?? []),
      statuses: Array.from(p.statuses ?? []).map((s) => ({
        kind: s.kind,
        remainingMs: s.remainingMs,
        magnitude: s.magnitude ?? 0,
        sourceId: s.sourceId ?? '',
      })),
      stunned: p.stunned,
      slowed: p.slowed,
      dotted: p.dotted,
      lastSeq: p.lastSeq,
    });
  });
  return out;
}

new App().start();
